<template>
	<div class="builder-view">
		<div class="builder-header">
			<h1>{{ projectName }}</h1>
			<div class="header-actions">
				<button @click="saveProject" class="save-btn">
					{{ $t('builder.save') }}
				</button>
				<button @click="showPreview = true" class="preview-btn">
					{{ $t('builder.preview') }}
				</button>
				<button @click="triggerBuild" class="build-btn">
					{{ $t('builder.build') }}
				</button>
			</div>
		</div>

		<div class="builder-main">
			<div class="editor-tabs">
				<button 
			     v-for="tab in tabs" 
			     :key="tab.id"
			     @click="activeTab = tab.id"
			     :class="{ active: activeTab === tab.id }"
			     >
			     <component :is="tab.icon" class="tab-icon" />
			     {{ $t(`builder.tabs.${tab.id}`) }}
				</button>
			</div>

			<div class="editor-container">
				<template-editor 
			     v-if="activeTab === 'template'" 
			     v-model="templateCode" 
			     :frameworks="selectedFrameworks"
			     />
					<style-editor 
v-if="activeTab === 'style'" 
v-model="styleCode"
:frameworks="selectedFrameworks" 
/>
<script-editor 
v-if="activeTab === 'script'" 
v-model="scriptCode"
:libraries="selectedLibraries" 
/>
<component-library 
v-if="activeTab === 'components'"
@add-component="addComponent"
/>
<dependency-manager 
v-if="activeTab === 'dependencies'"
:selected-dependencies="selectedLibraries"
@update-dependencies="updateDependencies"
/>
</div>
</div>

<PreviewView 
v-if="showPreview"
:template="templateCode"
:style="styleCode"
:script="scriptCode"
@close="showPreview = false"
/>
</div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import IconTemplate from '@/components/icons/IconTemplate.vue'
import IconStyle from '@/components/icons/IconStyle.vue'
import IconCode from '@/components/icons/IconCode.vue'
import IconComponents from '@/components/icons/IconComponents.vue'
import IconDependencies from '@/components/icons/IconDependencies.vue'
import TemplateEditor from '@/components/builder/TemplateEditor.vue'
import StyleEditor from '@/components/builder/StyleEditor.vue'
import ScriptEditor from '@/components/builder/ScriptEditor.vue'
import ComponentLibrary from '@/components/builder/ComponentLibrary.vue'
import DependencyManager from '@/components/builder/DependencyManager.vue'
import PreviewView from '@/views/PreviewView.vue'

const { t } = useI18n()

const props = defineProps({
	initialConfig: {
		type: Object,
		required: true
	}
})

const projectName = ref(props.initialConfig.projectName)
const selectedFrameworks = ref(props.initialConfig.frameworks)
const selectedLibraries = ref(props.initialConfig.libraries)
const templateCode = ref('')
const styleCode = ref('')
const scriptCode = ref('')
const showPreview = ref(false)

const tabs = [
{ id: 'template', icon: IconTemplate },
{ id: 'style', icon: IconStyle },
{ id: 'script', icon: IconCode },
{ id: 'components', icon: IconComponents },
{ id: 'dependencies', icon: IconDependencies }
]

const activeTab = ref('template')

// Initialize with default code based on selected frameworks
onMounted(() => {
	templateCode.value = generateInitialTemplate()
	styleCode.value = generateInitialStyle()
	scriptCode.value = generateInitialScript()
})

function generateInitialTemplate() {
	let template = '<div class="container">\n'
	template += `  <h1>${t('builder.welcome_message')}</h1>\n`

	if (selectedFrameworks.value.some(f => f.id === 'bootstrap')) {
		template += '  <button class="btn btn-primary">Bootstrap Button</button>\n'
	}

	if (selectedFrameworks.value.some(f => f.id === 'tailwind')) {
		template += '  <button class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">Tailwind Button</button>\n'
	}

	template += '</div>'
	return template
}

function generateInitialStyle() {
	let style = ''

	if (selectedFrameworks.value.some(f => f.id === 'bootstrap')) {
		style += '@import "bootstrap/dist/css/bootstrap.min.css";\n\n'
	}

	if (selectedFrameworks.value.some(f => f.id === 'tailwind')) {
		style += '@tailwind base;\n@tailwind components;\n@tailwind utilities;\n\n'
	}

	style += '.container {\n  max-width: 1200px;\n  margin: 0 auto;\n  padding: 2rem;\n}'
	return style
}

function generateInitialScript() {
	let script = 'export default {\n'
	script += '  name: "App",\n'

	if (selectedLibraries.value.some(l => l.id === 'pinia')) {
		script += '  // Pinia store access\n'
		script += '  computed: {\n'
		script += '    store() {\n'
		script += '      return useStore()\n'
		script += '    }\n'
		script += '  },\n'
	}

	script += '  data() {\n'
	script += '    return {\n'
	script += '      message: "' + t('builder.initial_message') + '"\n'
	script += '    }\n'
	script += '  }\n'
	script += '}'
	return script
}

function saveProject() {
	// Implement save logic
	console.log('Saving project...')
}

function triggerBuild() {
	// Implement build logic
	console.log('Building project...')
}

function addComponent(component) {
	// Add component to template
	templateCode.value += `\n<${component.name} />`
}

function updateDependencies(newDependencies) {
	selectedLibraries.value = newDependencies
}
</script>

<style scoped>
.builder-view {
	height: 100vh;
	display: flex;
	flex-direction: column;
}

.builder-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 1rem 2rem;
	background: #fff;
	border-bottom: 1px solid #eee;
}

.header-actions {
	display: flex;
	gap: 1rem;
}

.builder-main {
	display: flex;
	flex: 1;
	overflow: hidden;
}

.editor-tabs {
	width: 250px;
	background: #f5f5f5;
	border-right: 1px solid #ddd;
	padding: 1rem 0;
}

.editor-tabs button {
	display: flex;
	align-items: center;
	width: 100%;
	padding: 0.75rem 1.5rem;
	text-align: left;
	background: none;
	border: none;
	cursor: pointer;
}

.editor-tabs button:hover {
	background: #e9e9e9;
}

.editor-tabs button.active {
	background: #646cff;
	color: white;
}

.tab-icon {
	margin-right: 0.75rem;
	width: 1.25rem;
	height: 1.25rem;
}

.editor-container {
	flex: 1;
	overflow: auto;
}

.save-btn {
	background: #4CAF50;
	color: white;
}

.preview-btn {
	background: #2196F3;
	color: white;
}

.build-btn {
	background: #FF9800;
	color: white;
}
</style>
